/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, InputBase, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useAssistant } from "../containers/apps/drive/assistant";
import * as drive from "../api/drive";

/**
 * Navbar search, matching the accounts treatment: a static page index that
 * answers instantly, live file/folder results, and an "Ask AI" row that hands
 * the query to the assistant.
 */
const PAGES = [
  { title: "Overview", subtitle: "Home & AI assistant", path: "/", kw: "home dashboard assistant recent" },
  { title: "My Drive", subtitle: "Browse your files", path: "/drive", kw: "files folders upload storage" },
  { title: "Shared", subtitle: "Links and shared items", path: "/shared", kw: "share link public revoke" },
];

const NavSearch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openPanel } = useAssistant();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState({ files: [], folders: [] });
  const inputRef = useRef(null);

  const pageHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return PAGES.filter(
      (p) => p.title.toLowerCase().includes(q) || p.kw.includes(q)
    );
  }, [query]);

  // Debounced. Uses /drive/search rather than the owner-scoped list endpoints,
  // so team-owned and shared-with-me items show up here too, not just files
  // you personally uploaded.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setRemote({ files: [], folders: [] });
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const { files, folders } = await drive.search(q);
        if (active) setRemote({ files: files.slice(0, 5), folders: folders.slice(0, 5) });
      } catch {
        if (active) setRemote({ files: [], folders: [] });
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  // Cmd/Ctrl-K focuses search, the convention the rest of the suite uses.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => setOpen(false);

  const go = (path) => {
    close();
    setQuery("");
    navigate(path);
  };

  const askAi = () => {
    const seed = query.trim();
    close();
    setQuery("");
    openPanel(seed);
  };

  const rowSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    px: 1.5,
    py: 1,
    cursor: "pointer",
    borderRadius: "8px",
    color: theme.palette.text.color,
    "&:hover": { backgroundColor: theme.palette.tertiary.main },
  };

  const hasResults = pageHits.length || remote.folders.length || remote.files.length;

  return (
    <ClickAwayListener onClickAway={close}>
      <Box sx={{ position: "relative", flex: 1, maxWidth: 420, mr: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            height: 30,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.border.color}`,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <SearchIcon sx={{ fontSize: 17, color: theme.palette.secondaryText.color }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            placeholder="Search files and folders"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            sx={{
              flex: 1,
              fontSize: 13,
              color: theme.palette.text.color,
              "& input::placeholder": { color: theme.palette.secondaryText.color, opacity: 1 },
            }}
          />
          <Typography sx={{ fontSize: 11, color: theme.palette.secondaryText.color }}>⌘K</Typography>
        </Box>

        {open && query.trim() !== "" && (
          <Box
            sx={{
              position: "absolute",
              top: 38,
              left: 0,
              right: 0,
              zIndex: 200,
              p: 1,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.border.color}`,
              backgroundColor: theme.palette.secondary.main,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              maxHeight: 380,
              overflowY: "auto",
            }}
          >
            <Box sx={{ ...rowSx, color: "accent.main" }} onClick={askAi}>
              <AutoAwesomeIcon sx={{ fontSize: 17 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                Ask AI: “{query.trim()}”
              </Typography>
            </Box>

            {remote.folders.map((f) => (
              <Box key={f.id} sx={rowSx} onClick={() => go(`/drive/folders/${f.id}`)}>
                <FolderOutlinedIcon sx={{ fontSize: 17, color: "accent.main" }} />
                <Typography sx={{ fontSize: 13 }}>{f.name}</Typography>
              </Box>
            ))}

            {remote.files.map((f) => (
              <Box
                key={f.id}
                sx={rowSx}
                onClick={() => go(f.folder_id ? `/drive/folders/${f.folder_id}` : "/drive")}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 17, color: theme.palette.secondaryText.color }}
                />
                <Typography sx={{ fontSize: 13 }}>{f.name}</Typography>
              </Box>
            ))}

            {pageHits.map((p) => (
              <Box key={p.path} sx={rowSx} onClick={() => go(p.path)}>
                <FolderOutlinedIcon sx={{ fontSize: 17, color: theme.palette.secondaryText.color }} />
                <Box>
                  <Typography sx={{ fontSize: 13 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: 11, color: theme.palette.secondaryText.color }}>
                    {p.subtitle}
                  </Typography>
                </Box>
              </Box>
            ))}

            {!hasResults && (
              <Typography
                sx={{ px: 1.5, py: 1, fontSize: 12.5, color: theme.palette.secondaryText.color }}
              >
                No matches. Try “Ask AI” above.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default NavSearch;
